import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
    DialogTitle,
} from "@headlessui/react";
import { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import infoIconOutline from "../assets/icons/icon attention.svg";

import BottomNavigationButtons from "./BottomNavigationButtons";
import { type ConfirmOptions } from "../context/ConfirmContextProvider";

type ModalProps = ConfirmOptions &
    Required<Pick<ConfirmOptions, "onCancel">> & {
        isOpen: boolean;
        onClose: () => void;
    };

const ConfirmModal = ({
    isOpen,
    title,
    description,
    cancelText,
    confirmText,
    hideCancelButton,
    onCancel,
    onClose,
    onConfirm,
}: ModalProps) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = async () => {
        if (onConfirm) {
            setIsLoading(true);
            await onConfirm();
            setIsLoading(false);
        }
    };
    const handleCancel = async () => {
        if (onCancel) {
            setIsLoading(true);
            await onCancel();
            setIsLoading(false);
        }
    };
    useEffect(() => {
        const handleKeydown = (e: KeyboardEvent) => {
            if (onConfirm && isOpen && e.key === "Enter") {
                handleConfirm();
            }
        };

        window.addEventListener("keydown", handleKeydown);

        return () => window.removeEventListener("keydown", handleKeydown);
    }, [onConfirm, isOpen]);

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            <DialogBackdrop className="fixed inset-0 bg-black/70" />
            <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
                <DialogPanel className="relative flex flex-col w-full max-w-[800px] h-full max-h-[400px] bg-white p-6 rounded-[4px]">
                    <button
                        className="absolute top-0 right-0 m-3 size-9 rounded-full hover:bg-gray-100"
                        onClick={onClose}
                    >
                        <XMarkIcon className="size-full p-1.5" />
                    </button>
                    <div className="mt-10 flex flex-col items-center gap-2">
                        <img
                            src={infoIconOutline}
                            alt="info icon"
                            className="h-12"
                        />
                        <DialogTitle className="text-center font-semibold text-[22px] text-blue-dark">
                            {title}
                        </DialogTitle>
                        {description ? (
                            <p className="text-center text-base text-blue-dark">
                                {description}
                            </p>
                        ) : null}
                    </div>
                    <BottomNavigationButtons
                        cancelText={cancelText}
                        confirmText={confirmText}
                        hideCancelButton={hideCancelButton}
                        onCancelClick={handleCancel}
                        onConfirmClick={handleConfirm}
                        isConfirmLoading={isLoading}
                        classes={{ root: "justify-center mt-auto mb-4" }}
                    />
                </DialogPanel>
            </div>
        </Dialog>
    );
};

export default ConfirmModal;
